import { effectiveRir } from "./effortScale.js";
import type { LogSet } from "./schemas/logSet.js";
import { DEFAULT_WEEK_START_DAY, weekWindowStart } from "./weekWindow.js";

// #124: fatigue read from the sets, not from the RIR the athlete chose. Same
// exercise, same weight, same reps: if the reps in reserve fall week after
// week, the same work costs more, and fatigue is piling up. One week = one
// bar (the mean RIR of that week's sets of the group); a week with no tagged
// set at that load has no bar, it is NOT a zero (RIR 0 is a set to failure).
// Display only: nothing here reaches a save path.

/** Fewest distinct weeks a group needs before it says anything. */
export const RIR_AT_LOAD_MIN_WEEKS = 3;

export type RirAtLoadWeek = {
  /** Start of the calendar week, epoch ms (the athlete's week start). */
  weekStartMs: number;
  /** Mean effective RIR of the group's tagged sets that week. */
  meanRir: number;
  sets: number;
};

export type RirAtLoadSeries = {
  slug: string;
  /** Name stamped on the first set seen: a deleted-row fallback only. */
  snapshot: string | undefined;
  weightKg: number;
  reps: number;
  /** Oldest first, only the weeks that carry data. */
  weeks: RirAtLoadWeek[];
};

type Group = { slug: string; snapshot?: string; weightKg: number; reps: number; rows: Array<{ week: number; rir: number }> };

const roundKg = (kg: number): number => Math.round(kg * 100) / 100;

/**
 * The group (exercise, weight, reps) seen in the most distinct weeks of
 * [fromMs, toMs] with an effort tag, ties broken by tagged sets. null when
 * none reaches RIR_AT_LOAD_MIN_WEEKS. Sets with no effort tag are skipped
 * (unknown is not easy), as are sets without a load or reps.
 */
export const rirAtSameLoad = (
  sets: readonly LogSet[],
  window: { fromMs: number; toMs: number },
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): RirAtLoadSeries | null => {
  const groups = new Map<string, Group>();
  for (const s of sets) {
    if (s.kind !== "weighted" && s.kind !== "bodyweight") continue;
    if (s.reps == null || s.reps <= 0 || s.weight == null || s.weight <= 0) continue;
    const rir = effectiveRir(s);
    if (rir === null) continue;
    const atMs = Date.parse(s.performedAt);
    if (Number.isNaN(atMs) || atMs < window.fromMs || atMs > window.toMs) continue;
    const weightKg = roundKg(s.weight);
    const key = `${s.exerciseSlug}|${weightKg}|${s.reps}`;
    let g = groups.get(key);
    if (g === undefined) {
      g = { slug: s.exerciseSlug, snapshot: s.exerciseName, weightKg, reps: s.reps, rows: [] };
      groups.set(key, g);
    }
    g.rows.push({ week: weekWindowStart(atMs, weekStartDay), rir });
  }

  let best: { g: Group; weeks: number } | null = null;
  for (const g of groups.values()) {
    const weeks = new Set(g.rows.map((r) => r.week)).size;
    if (weeks < RIR_AT_LOAD_MIN_WEEKS) continue;
    if (
      best === null ||
      weeks > best.weeks ||
      (weeks === best.weeks && g.rows.length > best.g.rows.length)
    ) {
      best = { g, weeks };
    }
  }
  if (best === null) return null;

  const byWeek = new Map<number, { sum: number; n: number }>();
  for (const r of best.g.rows) {
    const w = byWeek.get(r.week) ?? { sum: 0, n: 0 };
    w.sum += r.rir;
    w.n += 1;
    byWeek.set(r.week, w);
  }
  const weeks = [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([weekStartMs, w]) => ({
      weekStartMs,
      meanRir: Math.round((w.sum / w.n) * 10) / 10,
      sets: w.n,
    }));
  return {
    slug: best.g.slug,
    snapshot: best.g.snapshot,
    weightKg: best.g.weightKg,
    reps: best.g.reps,
    weeks,
  };
};
